import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Replace, ListVideo } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CaptionItem } from "./CaptionItem";
import { updateCap, deleteCap, splitCap, mergeNext, addAt, replaceAll, sortCaps } from "@/lib/captionOps";
import { LINE_PRESETS, reflowCaptions } from "@/lib/lines";

const REFLOW_OPTIONS = Object.entries(LINE_PRESETS).filter(([k]) => k !== "off");

const ReelsLines = ({ captions, setCaptions }) => {
  const [preset, setPreset] = useState("punchy");
  const run = () => {
    const before = captions;
    const after = reflowCaptions(captions, preset);
    if (after.length === before.length) return toast.info("All captions are already short enough.");
    setCaptions(after);
    toast.success(`Split ${before.length} captions into ${after.length} short lines`, { action: { label: "Undo", onClick: () => setCaptions(before) } });
  };
  return (
    <div className="flex gap-2 items-center" data-testid="reels-lines">
      <select value={preset} onChange={(e) => setPreset(e.target.value)} aria-label="Short line length" data-testid="reflow-preset-select"
        className="flex-1 min-w-0 h-10 rounded-md bg-[var(--cc-elev)] border border-[var(--cc-border-strong)] px-2 text-sm">
        {REFLOW_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <Button size="sm" variant="secondary" className="h-10" onClick={run} data-testid="reflow-captions-btn">Make short Reels lines</Button>
    </div>
  );
};

const SearchReplace = ({ captions, setCaptions, find, setFind }) => {
  const [repl, setRepl] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const run = () => {
    const { caps, count } = replaceAll(captions, find, repl, matchCase);
    setCaptions(caps);
    toast[count ? "success" : "info"](count ? `Replaced ${count} match${count > 1 ? "es" : ""}` : "No matches found");
  };
  return (
    <div className="p-3 rounded-lg bg-[var(--cc-panel)] border border-[var(--cc-border)] space-y-2" data-testid="search-replace">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-2.5 top-3 text-[var(--cc-muted)]" />
          <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find…" aria-label="Find text" data-testid="search-input"
            className="w-full h-10 pl-8 pr-2 rounded-md bg-[var(--cc-bg)] border border-[var(--cc-border-strong)] text-sm deva" />
        </div>
        <input value={repl} onChange={(e) => setRepl(e.target.value)} placeholder="Replace with…" aria-label="Replace with" data-testid="replace-input"
          className="flex-1 h-10 px-2.5 rounded-md bg-[var(--cc-bg)] border border-[var(--cc-border-strong)] text-sm deva" />
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-[var(--cc-text-2)]"><input type="checkbox" checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)} data-testid="match-case-toggle" className="h-4 w-4" /> Match case</label>
        <Button size="sm" variant="secondary" onClick={run} disabled={!find} data-testid="replace-all-btn"><Replace className="h-4 w-4 mr-1" /> Replace all</Button>
      </div>
    </div>
  );
};

export const CaptionsPanel = ({ captions, setCaptions, time, seek, selectedId, setSelectedId, duration, videoRef, goUpload }) => {
  const [find, setFind] = useState("");
  const refs = useRef({});
  const activeId = useMemo(() => captions.find((c) => time >= c.start && time < c.end)?.id, [captions, time]);
  const shown = find ? captions.filter((c) => c.text.toLowerCase().includes(find.toLowerCase())) : captions;
  const flagged = captions.filter((c) => c.flag).length;

  useEffect(() => {
    const v = videoRef.current;
    if (!activeId || !v || v.paused) return;
    if (document.activeElement && document.activeElement.tagName === "TEXTAREA") return;
    refs.current[activeId]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId, videoRef]);

  useEffect(() => { if (selectedId) refs.current[selectedId]?.scrollIntoView({ block: "nearest" }); }, [selectedId]);

  const add = (t) => {
    const r = addAt(captions, t, duration);
    setCaptions(r.caps);
    setSelectedId(r.id);
  };
  const set = (id, patch) => setCaptions((cs) => (patch.start !== undefined ? sortCaps(updateCap(cs, id, patch)) : updateCap(cs, id, patch)));

  if (!captions.length) {
    return (
      <div className="text-center py-10 space-y-3" data-testid="captions-empty">
        <ListVideo className="h-10 w-10 mx-auto text-[var(--cc-muted)]" />
        <p className="font-semibold">No captions yet</p>
        <p className="text-sm text-[var(--cc-text-2)]">Generate captions from your video, import an SRT, or add one manually.</p>
        <div className="flex justify-center gap-2">
          <Button onClick={goUpload} data-testid="goto-generate-btn">Generate Captions</Button>
          <Button variant="outline" onClick={() => add(time)} data-testid="add-first-caption-btn"><Plus className="h-4 w-4 mr-1" /> Add manually</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold tracking-wider uppercase text-[var(--cc-text-2)]">Edit captions · {captions.length}{flagged ? <span className="text-rose-300 normal-case tracking-normal"> · {flagged} flagged</span> : null}</h2>
        <Button size="sm" variant="outline" onClick={() => add(time)} data-testid="add-caption-at-playhead-btn"><Plus className="h-4 w-4 mr-1" /> Add at playhead</Button>
      </div>
      <ReelsLines captions={captions} setCaptions={setCaptions} />
      <SearchReplace captions={captions} setCaptions={setCaptions} find={find} setFind={setFind} />
      {find && <p className="text-xs text-[var(--cc-text-2)]" data-testid="search-count">{shown.length} caption(s) contain “{find}”</p>}
      <ol className="space-y-2" data-testid="caption-list">
        {shown.map((c) => {
          const i = captions.indexOf(c);
          return (
            <CaptionItem key={c.id} c={c} i={i} active={c.id === activeId} selected={c.id === selectedId} isLast={i === captions.length - 1}
              itemRef={(el) => { refs.current[c.id] = el; }}
              onSelect={() => setSelectedId(c.id)}
              onChange={(patch) => set(c.id, patch)}
              onSeek={() => { seek(c.start + 0.01); videoRef.current?.play(); }}
              onSplit={(pos) => setCaptions(splitCap(captions, c.id, pos))}
              onMerge={() => setCaptions(mergeNext(captions, c.id))}
              onDelete={() => setCaptions(deleteCap(captions, c.id))}
              onAdd={() => add(c.end)} />
          );
        })}
      </ol>
    </div>
  );
};
